import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, Check, CheckCheck, ShieldCheck } from "lucide-react";

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayLabel(d) {
  const now = new Date();
  const yesterday = new Date(); yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
}

function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Returns the small tick indicator shown next to messages the current user sent. */
function SendStatusIcon({ read }) {
  if (read) return <CheckCheck size={12} className="text-white" />;
  return <Check size={12} />;
}

export default function MessagePane({ activeUser, messages, myUserId, text, setText, onSend, connected }) {
  const bottomRef = useRef(null);
  const [composeFocus, setComposeFocus] = useState(false);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!activeUser) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
        <MessageSquare size={42} className="text-primary/50 mb-3" />
        <div className="font-bold text-[#3D405B]">Pick a conversation</div>
        <p className="text-sm text-[#7A7D96] mt-1 max-w-sm">Your private chats with counsellors live here. Tap the search icon to start a new one.</p>
      </div>
    );
  }

  // Group messages by day
  let lastDayKey = null;

  return (
    <>
      <header className="p-4 border-b border-[#3D405B]/5 flex items-center gap-3 bg-[#FDFBF7]">
        {activeUser.picture ? (
          <img src={activeUser.picture} className="w-10 h-10 rounded-full object-cover" alt="" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center font-bold text-[#3D405B]">{activeUser.name?.[0] || "?"}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-[#3D405B] truncate" data-testid="chat-active-name">{activeUser.name || "Conversation"}</div>
          <div className="text-xs text-[#7A7D96] capitalize flex items-center gap-1.5">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? "bg-secondary" : "bg-[#7A7D96]/50"}`} />
            {activeUser.role}{connected ? "" : " · reconnecting…"}
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#7A7D96]">
          <ShieldCheck size={12} /> Private
        </span>
      </header>

      <div className="flex-1 overflow-y-auto p-5 space-y-1.5" style={{ maxHeight: "60vh" }}>
        {messages.length === 0 && (
          <div className="text-center text-sm text-[#7A7D96] py-10">Say hello — your conversation is private and confidential.</div>
        )}
        {messages.map((m, idx) => {
          const mine = m.sender_id === myUserId;
          const d = new Date(m.created_at);
          const key = d.toDateString();
          const showDivider = key !== lastDayKey;
          lastDayKey = key;
          const prev = idx > 0 ? messages[idx - 1] : null;
          const grouped = prev && prev.sender_id === m.sender_id && (new Date(m.created_at) - new Date(prev.created_at)) < 60 * 1000 && !showDivider;

          return (
            <div key={m.message_id}>
              {showDivider && (
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-[#3D405B]/8" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#7A7D96]">{dayLabel(d)}</span>
                  <div className="flex-1 h-px bg-[#3D405B]/8" />
                </div>
              )}
              <div className={`flex ${mine ? "justify-end" : "justify-start"} ${grouped ? "mt-0.5" : "mt-2"}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-primary text-white" : "bg-muted text-[#3D405B]"} ${mine ? (grouped ? "rounded-br-md" : "rounded-br-md") : (grouped ? "rounded-bl-md" : "rounded-bl-md")}`}>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                  <div className={`text-[10px] mt-1 flex items-center gap-1 justify-end ${mine ? "text-white/75" : "text-[#7A7D96]"}`}>
                    <span>{fmtTime(m.created_at)}</span>
                    {mine && <SendStatusIcon read={m.read} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={onSend} className="p-3 border-t border-[#3D405B]/5 bg-[#FDFBF7] flex items-end gap-2">
        <textarea
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setComposeFocus(true)}
          onBlur={() => setComposeFocus(false)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(e); } }}
          placeholder="Type a message — Enter to send, Shift+Enter for a new line"
          className={`flex-1 resize-none px-4 py-3 rounded-2xl bg-white border outline-none transition-all ${composeFocus ? "border-primary ring-2 ring-primary/30" : "border-[#3D405B]/15"}`}
          data-testid="chat-input"
        />
        <button type="submit" disabled={!text.trim()} className="p-3.5 rounded-full bg-primary text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50" data-testid="chat-send-btn" aria-label="Send message">
          <Send size={16} />
        </button>
      </form>
    </>
  );
}
